import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useAtom } from "jotai/index";
import { omit } from "lodash";
import set from "lodash/set";
import React, {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { useSysCfgCrossRoomsQuery } from "@/api/hooks/sys-cfg-hooks";
import { useRoomsListQuery } from "@/api/hooks/table-cache-hooks";
import { useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import { gameType } from "@/components/config-modals/router-edit-modal/constants";
import CustomDatetimeRangePicker from "@/components/custom-datetime-range-picker/custom-datetime-range-picker";
import { ROOM_CONFIGS_NAMES } from "@/lib/constants";
import { BooleanSelect } from "@/module/service-configs-module/statistics-visualization-module/components/boolean-select";
import { RangeFilter } from "@/module/service-configs-module/statistics-visualization-module/components/range-filter";
import { useStatVisualizationRooms } from "@/module/service-configs-module/statistics-visualization-module/hooks/use-stat-visualization-rooms";
import { HandsHistoryModal } from "@/module/service-configs-module/statistics-visualization-module/statistic-visualization-filters/hands-history-modal";
import { isSubmitLoading } from "@/module/service-configs-module/statistics-visualization-module/store";
import { MoneyFlowRequestDto, MoneyFlowRequestDtoPeriodEnum } from "@/shared/api";
import { useStatisticVisualizationFilters } from "@/shared/hooks/use-statistic-visualization-filters";
import { meStore } from "@/store/store";

const { RangePicker } = DatePicker;

const StyledRangePicker = styled(RangePicker)`
  .ant-picker-input {
    input {
      font-size: 12px;
    }
  }
`;

interface MoneyFlowRequestFormProps {
  initialState?: MoneyFlowRequestDto;
  onSubmit?: (data: MoneyFlowRequestDto) => void;
  onChange: (data: MoneyFlowRequestDto) => void;
  isStatsFilters?: boolean;
}

const autocompleteProps = {
  disableCloseOnSelect: true,
};

export const StatisticVisualizationFilters: React.FC<MoneyFlowRequestFormProps> = ({
  initialState,
  onSubmit,
  onChange,
  isStatsFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);
  const { t } = useTranslation();
  const isOwner = useIsOwnerOrSuperAdmin();
  const { data: playhubRoomsRs, isLoading: isRoomsLoading } = useRoomsListQuery();
  const { data: gameGroupsRs, isLoading: isSysCfgLoading } = useSysCfgCrossRoomsQuery(
    {
      config_name: ROOM_CONFIGS_NAMES.GAME_GROUP,
      rooms: playhubRoomsRs?.data?.rooms?.map((item) => item + "_eco") ?? [],
    },
    {
      enabled: !!playhubRoomsRs?.data?.rooms,
    }
  );
  const [me] = useAtom(meStore);
  const { gameGroupsData, statVisualizationRooms } = useStatVisualizationRooms({
    playhubRoomsResponse: playhubRoomsRs,
    gameGroupsResponse: gameGroupsRs,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    user: me!,
  });
  const [isOpenHandsHistoryModal, setIsModalHandsHistoryModal] = useState(false);

  const toggleHandsHistoryModal = () => {
    setIsModalHandsHistoryModal((prevState) => !prevState);
  };

  const [isLoading] = useAtom(isSubmitLoading);

  const {
    state,
    setState,
    errors,
    setError,
    setPeriod,
    updateGameCriteriaRoomId,
    updateGameCriteriaGameType,
    updateGameCriteriaStraddle,
    updateGameCriteriaAnte,
    updateGameCriteriaAntes,
    updateGameCriteriaBigBlinds,
    updateGameCriteriaTablesIds,
    updateGameCriteriaClubs,
    updateGameCriteriaTechnologiesPresence,
    updateHeroCriteriaAlliances,
    addGameCriteriaTechnologyPresence,
    updateGameCriteriaTechnologyPresenceName,
    updateHeroCriteriaTechnologies,
    updateHeroCriteriaGroups,
    updateHeroCriteriaClubs,
    updateHeroCriteriaPlayersIds,
    updateHeroCriteriaAiInstances,
    updateHeroCriteriaAiProfiles,
    updateHeroCriteriaAiIds,
    updateOpponentCriteriaTechnologies,
    updateOpponentCriteriaClubs,
    updateOpponentCriteriaGroups,
    updateOpponentCriteriaPlayersIds,
    updateOpponentCriteriaAiInstances,
    updateOpponentCriteriaAiProfiles,
    updateOpponentCriteriaAiIds,
    updateOpponentCriteriaClusteringPatterns,
    addSubOpponentCriteria,
    removeSubOpponentCriteria,
    updateSubOpponentCriteriaTechnologies,
    updateSubOpponentCriteriaGroups,
    updateSubOpponentCriteriaClubs,
    updateSubOpponentCriteriaPlayersIds,
    updateSubOpponentCriteriaAiInstances,
    updateSubOpponentCriteriaAiProfiles,
    updateSubOpponentCriteriaAiIds,
    updateSubOpponentCriteriaClusteringPatterns,

    anteOptions,
    setAnteOptions,
    bigBlindOptions,
    setBigBlindOptions,
    tableIdOptions,
    setTableIdOptions,
    alliancesOptions,
    setAlliancesOptions,
    clubOptions,
    setClubOptions,
    playerIdsOptions,
    setPlayerIOptions,
    aiInstancesOptions,
    setAiInstancesOptions,
    aiProfilesOptions,
    setAiProfilesOptions,
    aiIdsOptions,
    setAiIdsOptions,
    clusteringPatternOptions,
    setClusteringPatternOptions,
  } = useStatisticVisualizationFilters(initialState);

  const availableRoomsNGroups = me?.stat_visualization;

  const roomsOptions = useMemo(() => {
    if (isOwner && playhubRoomsRs?.data) {
      return playhubRoomsRs.data.rooms.map((room) => {
        return room;
      });
    } else if (availableRoomsNGroups) {
      return availableRoomsNGroups.map((item) => {
        return item.room_name;
      });
    }
    return [];
  }, [isOwner, playhubRoomsRs, availableRoomsNGroups]);
  const isHideRooms = roomsOptions.length === 1 && !isOwner;

  useEffect(() => {
    if (roomsOptions.length === 1) {
      updateGameCriteriaRoomId(roomsOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsOptions]);

  useEffect(() => {
    if (state.game_criteria.room_id) {
      setState((prevState) => {
        const newState = structuredClone(prevState);

        newState.hero_criteria.groups = [];
        newState.hero_criteria.technologies = [];

        newState.opponent_criteria.groups = [];
        newState.opponent_criteria.technologies = [];

        newState.sub_opponents_criteria.forEach((opp) => {
          opp.groups = [];
          opp.technologies = [];
        });

        return newState;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.game_criteria.room_id]);

  const groupOptions =
    gameGroupsData[state.game_criteria.room_id]?.map((item) => item.name) || [];
  const isHideGroups = groupOptions.length === 1 && !isOwner;

  const technologyOptions = useMemo(() => {
    if (isOwner) {
      return ["eco", "adv", "aicon"];
    }

    if (statVisualizationRooms) {
      const result: string[] = [];

      statVisualizationRooms.forEach((room) => {
        room.groups.forEach((group) => {
          const data = omit(group, "name");
          Object.keys(data).forEach((key) => {
            if (data[key as keyof typeof data]) {
              result.push(key);
            }
          });
        });
      });

      return Array.from(new Set(result));
    }
    return [];
  }, [isOwner, statVisualizationRooms]);

  const isHideTechnologies = technologyOptions.length === 1 && !isOwner;

  useEffect(() => {
    if (isHideTechnologies) {
      setState((prevState) => {
        const newState = structuredClone(prevState);

        newState.hero_criteria.technologies = [technologyOptions[0]];

        return newState;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHideTechnologies]);

  useEffect(() => {
    if (isHideGroups && groupOptions[0]) {
      updateHeroCriteriaGroups(groupOptions);
      setState((prevState) => {
        const newState = structuredClone(prevState);

        newState.opponent_criteria.groups = [];
        newState.sub_opponents_criteria.forEach((item) => {
          item.groups = [];
        });

        return newState;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHideGroups]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      let hasError = false;
      if (!state.game_criteria.room_id) {
        setError("gameCriteriaRoomId", t("fieldRequired"));
        hasError = true;
      }

      if (
        !state.game_criteria.timestamp_period.gte ||
        !state.game_criteria.timestamp_period.lte
      ) {
        setError("gameCriteriaTimestampPeriod", t("fieldRequired"));
        hasError = true;
      }

      if (hasError) {
        return;
      }
      onSubmit(state);
      setIsOpen(false);
    }
  };

  const handleOptionArrayKeyDown =
    <T = number,>(
      options: T[],
      setOptions: Dispatch<SetStateAction<T[]>>,
      setValue: Dispatch<SetStateAction<T[]>> | ((value: Array<T>) => void),
      currentValue: T[],
      type: "string" | "number"
    ) =>
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        const newValue = (event.target as HTMLInputElement).value;
        const isNumber = type === "number";
        const value = isNumber ? parseInt(newValue, 10) : newValue;
        if (isNumber && isNaN(value as number)) {
          return;
        }
        if (!options.includes(value as T)) {
          setOptions((prevState) => {
            return [...prevState, value] as T[];
          });
        }
        if (!currentValue.includes(value as T)) {
          setValue(Array.from(new Set([...currentValue, value])) as T[]);
        }
      }
    };

  const getOptionLabel = useCallback((option: number) => String(option), []);

  useEffect(() => {
    onChange(state);
  }, [state, onChange]);

  console.log("errors", errors);

  return (
    <>
      <Button
        variant="contained"
        onClick={toggle}
        sx={{ position: "absolute", right: "16px", top: "0px" }}
      >
        {t("filters")}
      </Button>
      <Dialog open={isOpen} onClose={toggle} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: "8px 16px",
              overflow: "auto",
              top: "64px",
              overflowX: "hidden",
            }}
            onSubmit={handleSubmit}
          >
            {/* Game Criteria */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Typography variant="h6">{t("gameCriteria")}</Typography>

              {isHideRooms ? null : (
                <FormControl fullWidth>
                  <InputLabel size="small">{t("roomId")}</InputLabel>
                  <Select
                    label={t("roomId")}
                    size="small"
                    value={state.game_criteria.room_id}
                    onChange={(e) => updateGameCriteriaRoomId(e.target.value)}
                    error={!!errors.gameCriteriaRoomId}
                    endAdornment={
                      isRoomsLoading || isSysCfgLoading ? (
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                      ) : undefined
                    }
                  >
                    {roomsOptions?.map((room) => (
                      <MenuItem key={room} value={room}>
                        {room}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl fullWidth>
                <InputLabel size="small">{t("gameType")}</InputLabel>
                <Select
                  label={t("gameType")}
                  size="small"
                  value={state.game_criteria.game_type}
                  onChange={(e) => updateGameCriteriaGameType(e.target.value)}
                  error={!!errors.gameCriteriaGameType}
                >
                  {gameType.map((el, key) => (
                    <MenuItem key={key} value={el.value}>
                      {el.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                onChange={(_event, newValue) => {
                  setState((prevState) => {
                    const data = structuredClone(prevState);
                    data.game_criteria.clubs = newValue;

                    return data;
                  });
                }}
                value={state.game_criteria.clubs}
                options={clubOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("clubs")}
                    variant="outlined"
                    onKeyDown={handleOptionArrayKeyDown(
                      clubOptions,
                      setClubOptions,
                      updateGameCriteriaClubs,
                      state.game_criteria.clubs,
                      "string"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              <Autocomplete
                onChange={(_event, newValue) => {
                  setState((prevState) => {
                    const data = structuredClone(prevState);
                    data.game_criteria.alliances = newValue;

                    return data;
                  });
                }}
                value={state.game_criteria.alliances}
                options={alliancesOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("alliances")}
                    variant="outlined"
                    onKeyDown={handleOptionArrayKeyDown(
                      alliancesOptions,
                      setAlliancesOptions,
                      updateHeroCriteriaAlliances,
                      state.game_criteria.alliances,
                      "string"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              {!isStatsFilters && (
                <FormControl fullWidth>
                  <InputLabel>{t("dateGranularity")}</InputLabel>
                  <Select
                    size="small"
                    value={state.period}
                    onChange={(e) =>
                      setPeriod(e.target.value as MoneyFlowRequestDtoPeriodEnum)
                    }
                    error={!!errors.period}
                  >
                    {Object.values(MoneyFlowRequestDtoPeriodEnum).map((period) => (
                      <MenuItem key={period} value={period}>
                        {period}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Stack gap={1}>
                <Typography sx={{ fontSize: "14px" }}>{t("timestampPeriod")}</Typography>
                <CustomDatetimeRangePicker
                  value={[
                    state.game_criteria.timestamp_period.gte
                      ? dayjs(Number(state.game_criteria.timestamp_period.gte))
                      : null,
                    state.game_criteria.timestamp_period.lte
                      ? dayjs(Number(state.game_criteria.timestamp_period.lte))
                      : null,
                  ]}
                  status={errors.gameCriteriaTimestampPeriod ? "error" : undefined}
                  onChange={(value) => {
                    setState((prevState) => {
                      const newState = { ...prevState };

                      if (errors.gameCriteriaTimestampPeriod) {
                        setError("gameCriteriaTimestampPeriod", "");
                      }

                      set(
                        newState,
                        `game_criteria.timestamp_period.gte`,
                        value?.[0] ? value[0].valueOf() : undefined
                      );
                      set(
                        newState,
                        `game_criteria.timestamp_period.lte`,
                        value?.[1] ? value[1].valueOf() : undefined
                      );

                      return newState;
                    });
                  }}
                />
              </Stack>

              <RangeFilter
                name="game_criteria.game_size"
                label={t("gameSize")}
                minValue={state.game_criteria.game_size?.gte}
                maxValue={state.game_criteria.game_size?.lte}
                minError={errors.gameCriteriaGameSize}
                maxError={errors.gameCriteriaGameSize}
                setState={setState}
                min={2}
                max={12}
              />

              <RangeFilter
                name="game_criteria.hero_count"
                label={t("heroCount")}
                minValue={state.game_criteria.hero_count?.gte}
                maxValue={state.game_criteria.hero_count?.lte}
                minError={errors.gameCriteriaHeroCount}
                maxError={errors.gameCriteriaHeroCount}
                setState={setState}
                max={12}
                min={1}
                // step={50}
              />

              <RangeFilter
                name="game_criteria.opponent_count"
                label={t("opponentCount")}
                minValue={state.game_criteria.opponent_count?.gte}
                maxValue={state.game_criteria.opponent_count?.lte}
                minError={errors.gameCriteriaHeroCount}
                maxError={errors.gameCriteriaHeroCount}
                setState={setState}
                max={12}
                min={0}
                // step={50}
              />

              <RangeFilter
                name="game_criteria.non_hero_count"
                label={t("nonHeroCount")}
                minValue={state.game_criteria.non_hero_count?.gte}
                maxValue={state.game_criteria.non_hero_count?.lte}
                minError={errors.gameCriteriaNonHeroCount}
                maxError={errors.gameCriteriaNonHeroCount}
                setState={setState}
                max={12}
                min={0}
                // step={50}
              />

              <BooleanSelect
                label={t("straddle")}
                value={state.game_criteria.straddle}
                onChange={updateGameCriteriaStraddle}
              />

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateGameCriteriaAntes(newValue);
                }}
                value={state.game_criteria.antes}
                options={anteOptions}
                multiple
                getOptionLabel={getOptionLabel}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("antes")}
                    variant="outlined"
                    error={!!errors.gameCriteriaAntes}
                    helperText={errors.gameCriteriaAntes}
                    onKeyDown={handleOptionArrayKeyDown(
                      anteOptions,
                      setAnteOptions,
                      updateGameCriteriaAntes,
                      state.game_criteria.antes,
                      "number"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              <BooleanSelect
                label={t("ante")}
                value={state.game_criteria.ante}
                onChange={updateGameCriteriaAnte}
              />

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateGameCriteriaBigBlinds(newValue);
                }}
                value={state.game_criteria.big_blinds}
                options={bigBlindOptions}
                multiple
                getOptionLabel={getOptionLabel}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("bigBlinds")}
                    variant="outlined"
                    error={!!errors.gameCriteriaBigBlinds}
                    helperText={errors.gameCriteriaBigBlinds}
                    onKeyDown={handleOptionArrayKeyDown(
                      bigBlindOptions,
                      setBigBlindOptions,
                      updateGameCriteriaBigBlinds,
                      state.game_criteria.big_blinds,
                      "number"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              <RangeFilter
                name="game_criteria.big_blind_usd_range"
                label={t("bigBlindUsdRange")}
                minValue={state.game_criteria.big_blind_usd_range?.gte}
                maxValue={state.game_criteria.big_blind_usd_range?.lte}
                minError={errors.gameCriteriaBigBlindUsdRange}
                maxError={errors.gameCriteriaBigBlindUsdRange}
                setState={setState}
                step={10}
                max={1000}
              />

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateGameCriteriaTablesIds(newValue);
                }}
                value={state.game_criteria.tables_ids}
                options={tableIdOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("tableIds")}
                    variant="outlined"
                    error={!!errors.gameCriteriaTablesIds}
                    helperText={errors.gameCriteriaTablesIds}
                    onKeyDown={handleOptionArrayKeyDown(
                      tableIdOptions,
                      setTableIdOptions,
                      updateGameCriteriaTablesIds,
                      state.game_criteria.tables_ids,
                      "string"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              {/* Technology Presence Section */}
              {isOwner && (
                <>
                  <Typography variant="subtitle1">{t("technologiesPresence")}</Typography>

                  {state.game_criteria.technologies_presence.map((tech, index) => (
                    <Box
                      key={index}
                      sx={{ border: "1px solid #ddd", p: 2, borderRadius: 1, mb: 2 }}
                    >
                      <Stack direction="column" spacing={2}>
                        {/*<TextField
                label="Technology Name"
                size="small"
                fullWidth
                value={tech.technology_name}
                onChange={(e) =>
                  updateGameCriteriaTechnologyPresenceName(index, e.target.value)
                }
                error={!!errors.gameCriteriaTechnologyPresence?.[index]?.technology_name}
                helperText={errors.gameCriteriaTechnologyPresence?.[index]?.technology_name}
              />*/}
                        <FormControl fullWidth>
                          <InputLabel>{t("technologyName")}</InputLabel>
                          <Select
                            label={t("technologyName")}
                            size="small"
                            value={tech.technology_name}
                            onChange={(e) =>
                              updateGameCriteriaTechnologyPresenceName(index, e.target.value)
                            }
                            error={
                              !!errors.gameCriteriaTechnologyPresence?.[index]?.technology_name
                            }
                          >
                            {technologyOptions.map((item) => {
                              return (
                                <MenuItem key={item} value={item}>
                                  {item}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>

                        <RangeFilter
                          name={`game_criteria.technologies_presence.${index}.presence_range`}
                          minValue={tech.presence_range.gte}
                          maxValue={tech.presence_range.lte}
                          minError={
                            errors.gameCriteriaTechnologyPresence?.[index]?.presence_range
                          }
                          maxError={
                            errors.gameCriteriaTechnologyPresence?.[index]?.presence_range
                          }
                          setState={setState}
                          step={1}
                          min={1}
                          max={12}
                        />

                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            const newTechnologies = [
                              ...state.game_criteria.technologies_presence,
                            ];
                            newTechnologies.splice(index, 1);
                            updateGameCriteriaTechnologiesPresence(newTechnologies);
                          }}
                        >
                          {t("remove")}
                        </Button>
                      </Stack>
                    </Box>
                  ))}

                  <Button
                    variant="outlined"
                    onClick={() => addGameCriteriaTechnologyPresence()}
                  >
                    {t("addTechnologyPresence")}
                  </Button>

                  <RangeFilter
                    name="game_criteria.no_technology_presence_range"
                    label={t("noTechnologyPresenceRange")}
                    minValue={state.game_criteria.no_technology_presence_range?.gte}
                    maxValue={state.game_criteria.no_technology_presence_range?.lte}
                    minError={errors.gameCriteriaNoTechnologyPresenceRange}
                    maxError={errors.gameCriteriaNoTechnologyPresenceRange}
                    setState={setState}
                    min={0}
                    max={12}
                  />
                </>
              )}
            </div>

            {/* Hero Criteria */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Typography variant="h6">{t("heroCriteria")}</Typography>

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateHeroCriteriaGroups(newValue);
                }}
                value={state.hero_criteria.groups}
                options={groupOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("groups")}
                    variant="outlined"
                    error={!!errors.heroCriteriaGroups}
                    helperText={errors.heroCriteriaGroups}
                  />
                )}
                {...autocompleteProps}
              />

              {(isOwner
                ? true
                : !!state.hero_criteria.groups.length && !isHideTechnologies) && (
                <Autocomplete
                  onChange={(_event, newValue) => {
                    updateHeroCriteriaTechnologies(newValue);
                  }}
                  value={state.hero_criteria.technologies}
                  options={technologyOptions}
                  multiple
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("technologies")}
                      variant="outlined"
                      error={!!errors.heroCriteriaGroups}
                      helperText={errors.heroCriteriaGroups}
                    />
                  )}
                  {...autocompleteProps}
                />
              )}

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateHeroCriteriaTechnologies(newValue);
                }}
                value={state.hero_criteria.clubs}
                options={clubOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("clubs")}
                    variant="outlined"
                    error={!!errors.heroCriteriaClubs}
                    helperText={errors.heroCriteriaClubs}
                    onKeyDown={handleOptionArrayKeyDown(
                      clubOptions,
                      setClubOptions,
                      updateHeroCriteriaClubs,
                      state.hero_criteria.clubs,
                      "string"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateHeroCriteriaPlayersIds(newValue);
                }}
                value={state.hero_criteria.players_ids}
                options={playerIdsOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("playerIds")}
                    variant="outlined"
                    error={!!errors.heroCriteriaPlayersIds}
                    helperText={errors.heroCriteriaPlayersIds}
                    onKeyDown={handleOptionArrayKeyDown(
                      playerIdsOptions,
                      setPlayerIOptions,
                      updateHeroCriteriaPlayersIds,
                      state.hero_criteria.players_ids,
                      "string"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              {isOwner && (
                <>
                  <Autocomplete
                    onChange={(_event, newValue) => {
                      updateHeroCriteriaAiInstances(newValue);
                    }}
                    value={state.hero_criteria.ai_instances}
                    options={aiInstancesOptions}
                    multiple
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t("aiInstances")}
                        variant="outlined"
                        error={!!errors.heroCriteriaAiInstances}
                        helperText={errors.heroCriteriaAiInstances}
                        onKeyDown={handleOptionArrayKeyDown(
                          aiInstancesOptions,
                          setAiInstancesOptions,
                          updateHeroCriteriaAiInstances,
                          state.hero_criteria.ai_instances,
                          "string"
                        )}
                      />
                    )}
                    {...autocompleteProps}
                  />

                  <Autocomplete
                    onChange={(_event, newValue) => {
                      updateHeroCriteriaAiProfiles(newValue);
                    }}
                    value={state.hero_criteria.ai_profiles}
                    options={aiProfilesOptions}
                    multiple
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t("aiProfiles")}
                        variant="outlined"
                        error={!!errors.heroCriteriaAiProfiles}
                        helperText={errors.heroCriteriaAiProfiles}
                        onKeyDown={handleOptionArrayKeyDown(
                          aiProfilesOptions,
                          setAiProfilesOptions,
                          updateHeroCriteriaAiProfiles,
                          state.hero_criteria.ai_profiles,
                          "string"
                        )}
                      />
                    )}
                    {...autocompleteProps}
                  />

                  <Autocomplete
                    onChange={(_event, newValue) => {
                      updateHeroCriteriaAiIds(newValue);
                    }}
                    value={state.hero_criteria.ai_ids}
                    options={aiProfilesOptions}
                    multiple
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t("aiIds")}
                        variant="outlined"
                        error={!!errors.heroCriteriaAiIds}
                        helperText={errors.heroCriteriaAiIds}
                        onKeyDown={handleOptionArrayKeyDown(
                          aiIdsOptions,
                          setAiIdsOptions,
                          updateHeroCriteriaAiIds,
                          state.hero_criteria.ai_ids,
                          "string"
                        )}
                      />
                    )}
                    {...autocompleteProps}
                  />
                </>
              )}

              {/*<TextField
          label="Clustering Patterns (comma separated)"
          size="small"
          fullWidth
          value={state.hero_criteria.clustering_patterns.join(", ")}
          onChange={(e) =>
            handleStringArrayChange(e.target.value, updateHeroCriteriaClusteringPatterns)
          }
          error={!!errors.heroCriteriaClusteringPatterns}
          helperText={errors.heroCriteriaClusteringPatterns}
        />*/}
            </div>

            {/* Opponent Criteria */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Typography variant="h6">{t("opponentCriteria")}</Typography>

              {isHideGroups ? null : (
                <Autocomplete
                  onChange={(_event, newValue) => {
                    updateOpponentCriteriaGroups(newValue);
                  }}
                  value={state.opponent_criteria.groups}
                  options={groupOptions}
                  multiple
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("groups")}
                      variant="outlined"
                      error={!!errors.opponentCriteriaGroups}
                      helperText={errors.opponentCriteriaGroups}
                    />
                  )}
                  {...autocompleteProps}
                />
              )}

              {(isOwner
                ? true
                : !!state.opponent_criteria.groups.length && !!technologyOptions.length) && (
                <Autocomplete
                  onChange={(_event, newValue) => {
                    updateOpponentCriteriaTechnologies(newValue);
                  }}
                  value={state.opponent_criteria.technologies}
                  options={technologyOptions}
                  multiple
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("technologies")}
                      variant="outlined"
                      error={!!errors.opponentCriteriaTechnologies}
                      helperText={errors.opponentCriteriaTechnologies}
                    />
                  )}
                  {...autocompleteProps}
                />
              )}

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateOpponentCriteriaClubs(newValue);
                }}
                value={state.opponent_criteria.clubs}
                options={clubOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("clubs")}
                    variant="outlined"
                    error={!!errors.opponentCriteriaClubs}
                    helperText={errors.opponentCriteriaClubs}
                    onKeyDown={handleOptionArrayKeyDown(
                      clubOptions,
                      setClubOptions,
                      updateOpponentCriteriaClubs,
                      state.opponent_criteria.clubs,
                      "string"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              <Autocomplete
                onChange={(_event, newValue) => {
                  updateOpponentCriteriaPlayersIds(newValue);
                }}
                value={state.opponent_criteria.players_ids}
                options={playerIdsOptions}
                multiple
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("playerIds")}
                    variant="outlined"
                    error={!!errors.opponentCriteriaPlayersIds}
                    helperText={errors.opponentCriteriaPlayersIds}
                    onKeyDown={handleOptionArrayKeyDown(
                      playerIdsOptions,
                      setPlayerIOptions,
                      updateOpponentCriteriaPlayersIds,
                      state.opponent_criteria.players_ids,
                      "string"
                    )}
                  />
                )}
                {...autocompleteProps}
              />

              {isOwner && (
                <>
                  {isOwner && (
                    <>
                      <Autocomplete
                        onChange={(_event, newValue) => {
                          updateOpponentCriteriaAiInstances(newValue);
                        }}
                        value={state.opponent_criteria.ai_instances}
                        options={aiInstancesOptions}
                        multiple
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t("aiInstances")}
                            variant="outlined"
                            error={!!errors.opponentCriteriaAiInstances}
                            helperText={errors.opponentCriteriaAiInstances}
                            onKeyDown={handleOptionArrayKeyDown(
                              aiInstancesOptions,
                              setAiInstancesOptions,
                              updateOpponentCriteriaAiInstances,
                              state.opponent_criteria.ai_instances,
                              "string"
                            )}
                          />
                        )}
                        {...autocompleteProps}
                      />

                      <Autocomplete
                        onChange={(_event, newValue) => {
                          updateOpponentCriteriaAiProfiles(newValue);
                        }}
                        value={state.opponent_criteria.ai_profiles}
                        options={aiProfilesOptions}
                        multiple
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t("aiProfiles")}
                            variant="outlined"
                            error={!!errors.opponentCriteriaAiProfiles}
                            helperText={errors.opponentCriteriaAiProfiles}
                            onKeyDown={handleOptionArrayKeyDown(
                              aiProfilesOptions,
                              setAiProfilesOptions,
                              updateOpponentCriteriaAiProfiles,
                              state.opponent_criteria.ai_profiles,
                              "string"
                            )}
                          />
                        )}
                        {...autocompleteProps}
                      />

                      <Autocomplete
                        onChange={(_event, newValue) => {
                          updateOpponentCriteriaAiIds(newValue);
                        }}
                        value={state.opponent_criteria.ai_ids}
                        options={aiIdsOptions}
                        multiple
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t("aiIds")}
                            variant="outlined"
                            error={!!errors.opponentCriteriaAiIds}
                            helperText={errors.opponentCriteriaAiIds}
                            onKeyDown={handleOptionArrayKeyDown(
                              aiIdsOptions,
                              setAiIdsOptions,
                              updateOpponentCriteriaAiIds,
                              state.opponent_criteria.ai_ids,
                              "string"
                            )}
                          />
                        )}
                        {...autocompleteProps}
                      />

                      <Autocomplete
                        onChange={(_event, newValue) => {
                          updateOpponentCriteriaClusteringPatterns(newValue);
                        }}
                        value={state.opponent_criteria.clustering_patterns}
                        options={clusteringPatternOptions}
                        multiple
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t("clusteringPatterns")}
                            variant="outlined"
                            error={!!errors.opponentCriteriaClusteringPatterns}
                            helperText={errors.opponentCriteriaClusteringPatterns}
                            onKeyDown={handleOptionArrayKeyDown(
                              clusteringPatternOptions,
                              setClusteringPatternOptions,
                              updateOpponentCriteriaClusteringPatterns,
                              state.opponent_criteria.clustering_patterns,
                              "string"
                            )}
                          />
                        )}
                        {...autocompleteProps}
                      />
                    </>
                  )}
                </>
              )}
            </div>

            {/* Sub Opponents Criteria */}
            {!isStatsFilters && (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Typography variant="h6">{t("subOpponentsCriteria")}</Typography>

                {state.sub_opponents_criteria.map((subOpponent, index) => (
                  <Box
                    key={index}
                    sx={{ border: "1px solid #ddd", p: 2, borderRadius: 1, mb: 2 }}
                  >
                    <Typography variant="subtitle1">
                      {t("subOpponent")} {index + 1}
                    </Typography>

                    <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                      {isHideGroups ? null : (
                        <Autocomplete
                          onChange={(_event, newValue) => {
                            updateSubOpponentCriteriaGroups(index)(newValue as string[]);
                          }}
                          value={subOpponent.groups}
                          options={groupOptions}
                          multiple
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label={t("groups")}
                              variant="outlined"
                              error={!!errors.subOpponentsCriteria?.[index]?.groups}
                              helperText={errors.subOpponentsCriteria?.[index]?.groups}
                            />
                          )}
                          {...autocompleteProps}
                        />
                      )}

                      {(isOwner
                        ? true
                        : !!subOpponent.groups.length && !!technologyOptions.length) && (
                        <Autocomplete
                          onChange={(_event, newValue) => {
                            updateSubOpponentCriteriaTechnologies(index)(newValue as string[]);
                          }}
                          value={subOpponent.technologies}
                          options={technologyOptions}
                          multiple
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label={t("technologies")}
                              variant="outlined"
                              error={!!errors.subOpponentsCriteria?.[index]?.technologies}
                              helperText={errors.subOpponentsCriteria?.[index]?.technologies}
                            />
                          )}
                          {...autocompleteProps}
                        />
                      )}

                      <Autocomplete
                        onChange={(_event, newValue) => {
                          updateSubOpponentCriteriaClubs(index)(newValue as string[]);
                        }}
                        value={subOpponent.clubs}
                        options={clubOptions}
                        multiple
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t("clubs")}
                            variant="outlined"
                            error={!!errors.subOpponentsCriteria?.[index]?.clubs}
                            helperText={errors.subOpponentsCriteria?.[index]?.clubs}
                            onKeyDown={handleOptionArrayKeyDown(
                              clubOptions,
                              setClubOptions,
                              updateSubOpponentCriteriaClubs(index),
                              subOpponent.clubs,
                              "string"
                            )}
                          />
                        )}
                        {...autocompleteProps}
                      />

                      <Autocomplete
                        onChange={(_event, newValue) => {
                          updateSubOpponentCriteriaPlayersIds(index)(newValue as string[]);
                        }}
                        value={subOpponent.players_ids}
                        options={playerIdsOptions}
                        multiple
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t("playerIds")}
                            variant="outlined"
                            error={!!errors.subOpponentsCriteria?.[index]?.playersIds}
                            helperText={errors.subOpponentsCriteria?.[index]?.playersIds}
                            onKeyDown={handleOptionArrayKeyDown(
                              playerIdsOptions,
                              setPlayerIOptions,
                              updateSubOpponentCriteriaPlayersIds(index),
                              subOpponent.players_ids,
                              "string"
                            )}
                          />
                        )}
                        {...autocompleteProps}
                      />

                      {isOwner && (
                        <>
                          {isOwner && (
                            <>
                              <Autocomplete
                                onChange={(_event, newValue) => {
                                  updateSubOpponentCriteriaAiInstances(index)(
                                    newValue as string[]
                                  );
                                }}
                                value={subOpponent.ai_instances}
                                options={aiInstancesOptions}
                                multiple
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    label={t("aiInstances")}
                                    variant="outlined"
                                    error={!!errors.subOpponentsCriteria?.[index]?.aiInstances}
                                    helperText={
                                      errors.subOpponentsCriteria?.[index]?.aiInstances
                                    }
                                    onKeyDown={handleOptionArrayKeyDown(
                                      aiInstancesOptions,
                                      setAiInstancesOptions,
                                      updateSubOpponentCriteriaAiInstances(index),
                                      subOpponent.ai_instances,
                                      "string"
                                    )}
                                  />
                                )}
                                {...autocompleteProps}
                              />

                              <Autocomplete
                                onChange={(_event, newValue) => {
                                  updateSubOpponentCriteriaAiProfiles(index)(
                                    newValue as string[]
                                  );
                                }}
                                value={subOpponent.ai_profiles}
                                options={aiProfilesOptions}
                                multiple
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    label={t("aiProfiles")}
                                    variant="outlined"
                                    error={!!errors.subOpponentsCriteria?.[index]?.aiProfiles}
                                    helperText={
                                      errors.subOpponentsCriteria?.[index]?.aiProfiles
                                    }
                                    onKeyDown={handleOptionArrayKeyDown(
                                      aiProfilesOptions,
                                      setAiProfilesOptions,
                                      updateSubOpponentCriteriaAiProfiles(index),
                                      subOpponent.ai_profiles,
                                      "string"
                                    )}
                                  />
                                )}
                                {...autocompleteProps}
                              />

                              <Autocomplete
                                onChange={(_event, newValue) => {
                                  updateSubOpponentCriteriaAiIds(index)(newValue as string[]);
                                }}
                                value={subOpponent.ai_ids}
                                options={aiIdsOptions}
                                multiple
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    label={t("aiIds")}
                                    variant="outlined"
                                    error={!!errors.subOpponentsCriteria?.[index]?.aiIds}
                                    helperText={errors.subOpponentsCriteria?.[index]?.aiIds}
                                    onKeyDown={handleOptionArrayKeyDown(
                                      aiIdsOptions,
                                      setAiIdsOptions,
                                      updateSubOpponentCriteriaAiIds(index),
                                      subOpponent.ai_ids,
                                      "string"
                                    )}
                                  />
                                )}
                                {...autocompleteProps}
                              />

                              <Autocomplete
                                onChange={(_event, newValue) => {
                                  updateSubOpponentCriteriaClusteringPatterns(index)(
                                    newValue as string[]
                                  );
                                }}
                                value={subOpponent.clustering_patterns}
                                options={clusteringPatternOptions}
                                multiple
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    label={t("clusteringPatterns")}
                                    variant="outlined"
                                    error={
                                      !!errors.subOpponentsCriteria?.[index]
                                        ?.clusteringPatterns
                                    }
                                    helperText={
                                      errors.subOpponentsCriteria?.[index]?.clusteringPatterns
                                    }
                                    onKeyDown={handleOptionArrayKeyDown(
                                      clusteringPatternOptions,
                                      setClusteringPatternOptions,
                                      updateSubOpponentCriteriaClusteringPatterns(index),
                                      subOpponent.clustering_patterns,
                                      "string"
                                    )}
                                  />
                                )}
                                {...autocompleteProps}
                              />
                            </>
                          )}
                        </>
                      )}

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeSubOpponentCriteria(index)}
                      >
                        {t("removeSubOpponent")}
                      </Button>
                    </Stack>
                  </Box>
                ))}

                <Button variant="outlined" onClick={() => addSubOpponentCriteria()}>
                  {t("addSubOpponent")}
                </Button>
              </div>
            )}

            <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", mt: 2 }}>
              {isOwner && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={toggleHandsHistoryModal}
                  sx={{
                    height: "34px",
                  }}
                  disabled={!state.game_criteria.room_id}
                >
                  {t("exportHandsHistory")}
                </Button>
              )}

              <Box id="statisticVisualizationFiltersButtonsContainer" />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{
                  height: "34px",
                }}
                loading={isLoading}
              >
                {t("submit")}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <HandsHistoryModal
        isOpen={isOpenHandsHistoryModal}
        onClose={toggleHandsHistoryModal}
        state={state}
      />
    </>
  );
};
